import { createFileRoute, Link } from '@tanstack/react-router';
import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react';
import { useRef } from 'react';
import { ArrowRight, Sun, Leaf, MapPin, Truck, ShieldCheck, Sprout } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ProductCard } from '@/components/product/product-card';
import { ProductGridSkeleton } from '@/components/ui/skeleton';
import { Reveal, Stagger, StaggerItem, transitions } from '@/components/motion/primitives';
import { useFeaturedProducts, useCategories } from '@/lib/queries';
import logoSrc from '/brand/sreerasa-logo.png';

export const Route = createFileRoute('/')({
  component: LandingPage,
});

function LandingPage() {
  return (
    <>
      <Hero />
      <PromiseStrip />
      <CategoryRail />
      <FreshToday />
      <HowItWorks />
      <FarmerCta />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Hero
   ═══════════════════════════════════════════════════════════════════════ */

function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();

  // Parallax: the logo drifts up and fades as you scroll past. Gated on
  // reduced-motion — scroll-linked parallax is exactly the vestibular
  // trigger WCAG 2.3.3 is about.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  const artY = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -60]);
  const artOpacity = useTransform(scrollYProgress, [0, 0.8], [1, reduced ? 1 : 0.2]);

  return (
    <section ref={sectionRef} className="relative overflow-hidden">
      {/* Warm field wash behind the fold */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-field-200 via-field-100 to-soil-50" />
      <div className="absolute inset-0 -z-10 bg-grain opacity-60" />

      {/* Soft colour blooms. aria-hidden — purely atmosphere. */}
      <div
        className="absolute -left-32 top-10 -z-10 size-96 rounded-full bg-farm-300/25 blur-3xl"
        aria-hidden
      />
      <div
        className="absolute -right-24 top-40 -z-10 size-80 rounded-full bg-sun-400/20 blur-3xl"
        aria-hidden
      />

      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:py-24 lg:px-8">
        {/* ── Copy ── */}
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={transitions.base}
          >
            <Badge variant="farm" size="lg" className="shadow-soft">
              <Sprout aria-hidden />
              Farm to home · Telangana
            </Badge>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...transitions.slow, delay: 0.08 }}
            className="mt-6 font-display text-hero font-black text-soil-900"
          >
            Food that
            <br />
            <span className="text-gradient-brand">remembers</span>
            <br />
            where it grew.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...transitions.slow, delay: 0.16 }}
            className="mt-6 max-w-lg text-lg leading-relaxed text-soil-600"
          >
            Mangoes ripened in hay, not carbide. Rice milled the week you order it. Vegetables cut
            the morning they reach you. Every listing names the farm, the village, and the farmer.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...transitions.slow, delay: 0.24 }}
            className="mt-9 flex flex-wrap gap-3"
          >
            <Link to="/shop">
              <Button size="lg" className="group">
                Shop today's harvest
                <ArrowRight
                  className="transition-transform duration-300 group-hover:translate-x-1"
                  aria-hidden
                />
              </Button>
            </Link>
            <Link to="/farms">
              <Button size="lg" variant="outline">
                Meet the farms
              </Button>
            </Link>
          </motion.div>

          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
          >
            {[
              { value: 'Zero', label: 'carbide, ever' },
              { value: 'Named', label: 'farm on every item' },
              { value: 'Next day', label: 'across Hyderabad' },
            ].map((stat) => (
              <div key={stat.label}>
                <dt className="font-display text-2xl font-bold text-farm-700">{stat.value}</dt>
                <dd className="text-sm text-soil-500">{stat.label}</dd>
              </div>
            ))}
          </motion.dl>
        </div>

        {/* ── Art ── */}
        <motion.div
          style={{ y: artY, opacity: artOpacity }}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ ...transitions.slow, delay: 0.1 }}
          className="relative"
        >
          <div className="relative mx-auto max-w-md lg:max-w-none">
            <img
              src={logoSrc}
              alt="SreeRasa — a farmer ploughing with bullocks beneath a rising sun"
              className="w-full drop-shadow-2xl"
              fetchPriority="high"
            />
          </div>

          {/*
            Floating proof chips. Hidden on small screens where they'd collide
            with the artwork.

            These state the PROMISE, not a specific farm. A chip naming a real
            village reads as a claim that we source from there — fine as seed
            data, misleading on a shop window, and it would go stale the moment
            that farm stopped selling. Real provenance belongs on the listing,
            where it comes from the database and is true.
          */}
          <motion.div
            className="absolute -left-2 top-8 hidden lg:block"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Badge variant="field" size="lg" className="shadow-lift backdrop-blur-sm">
              <Sun aria-hidden /> Ripened in hay, never carbide
            </Badge>
          </motion.div>

          <motion.div
            className="absolute -right-2 bottom-16 hidden lg:block"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          >
            <Badge variant="farm" size="lg" className="shadow-lift backdrop-blur-sm">
              <MapPin aria-hidden /> Every listing names its farm
            </Badge>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Promise strip
   ═══════════════════════════════════════════════════════════════════════ */

const PROMISES = [
  {
    icon: Sun,
    title: 'Ripened, not gassed',
    body: 'Hay and time, the way it was done before calcium carbide existed. It takes nine days longer and tastes like the difference.',
  },
  {
    icon: MapPin,
    title: 'A farm with a name',
    body: 'Every listing carries the village, the district, and the farmer. Not "sourced locally" — an actual place you could drive to.',
  },
  {
    icon: ShieldCheck,
    title: 'Verified before listed',
    body: 'We visit and verify a farm before its produce goes live. A farmer cannot publish their own listing, and that is the point.',
  },
  {
    icon: Truck,
    title: 'Picked, then delivered',
    body: 'Most produce is harvested the same day it ships. Order before 6pm and it reaches you next morning.',
  },
];

function PromiseStrip() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
      <Reveal className="mb-12 text-center">
        <h2 className="font-display text-display font-black text-soil-900">
          Everyone says <span className="text-soil-400 line-through">"farm fresh"</span>
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-lg text-soil-600">
          Nobody shows you the farm. We do — because a promise you can't check isn't a promise.
        </p>
      </Reveal>

      <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {PROMISES.map((promise) => (
          <StaggerItem key={promise.title}>
            <motion.div
              whileHover={{ y: -6 }}
              transition={transitions.spring}
              className="h-full rounded-card border border-soil-200 bg-white p-6 shadow-soft transition-shadow hover:shadow-lift"
            >
              <div className="flex size-11 items-center justify-center rounded-xl bg-farm-50 text-farm-600">
                <promise.icon className="size-5" aria-hidden />
              </div>
              <h3 className="mt-4 font-display text-base font-bold text-soil-900">
                {promise.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-soil-600">{promise.body}</p>
            </motion.div>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Categories
   ═══════════════════════════════════════════════════════════════════════ */

function CategoryRail() {
  const { data: categories, isLoading } = useCategories();

  return (
    <section className="border-y border-soil-200 bg-white py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <h2 className="font-display text-2xl font-bold text-soil-900">What's growing</h2>
        </Reveal>

        {/* Horizontal rail on mobile, wrapped grid on desktop. scrollbar-none
            keeps the rail clean; it's still swipeable and keyboard-scrollable. */}
        <div className="scrollbar-none mt-6 flex gap-3 overflow-x-auto pb-2 md:flex-wrap md:overflow-visible">
          {isLoading
            ? Array.from({ length: 8 }, (_, i) => (
                <div key={i} className="skeleton h-24 w-36 shrink-0 rounded-card" />
              ))
            : categories?.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...transitions.base, delay: index * 0.04 }}
                >
                  <Link
                    to="/shop"
                    search={{ category: category.slug }}
                    className="group flex h-24 w-36 shrink-0 flex-col justify-end rounded-card border border-soil-200 bg-gradient-to-br from-field-100 to-white p-4 transition-all hover:-translate-y-1 hover:border-farm-300 hover:shadow-lift"
                  >
                    <Leaf
                      className="mb-auto size-4 text-farm-500 transition-transform group-hover:scale-110"
                      aria-hidden
                    />
                    <p className="text-sm font-semibold text-soil-900">{category.name}</p>
                    {category.name_te && (
                      <p className="truncate text-xs text-soil-500">{category.name_te}</p>
                    )}
                  </Link>
                </motion.div>
              ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Fresh today
   ═══════════════════════════════════════════════════════════════════════ */

function FreshToday() {
  const { data: products, isLoading, isError } = useFeaturedProducts(8);

  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
      <Reveal className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-display font-black text-soil-900">Fresh today</h2>
          <p className="mt-2 text-soil-600">Picked this week. When it's gone, it's gone.</p>
        </div>
        <Link to="/shop">
          <Button variant="outline" className="group">
            See everything
            <ArrowRight className="transition-transform group-hover:translate-x-1" aria-hidden />
          </Button>
        </Link>
      </Reveal>

      {isLoading ? (
        <ProductGridSkeleton count={8} />
      ) : isError ? (
        <EmptyState
          title="Couldn't load the harvest"
          body="Something went wrong fetching today's produce. Refresh and it'll probably sort itself out."
        />
      ) : products && products.length > 0 ? (
        <div className="grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4">
          {products.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>
      ) : (
        <EmptyState
          title="Nothing listed yet"
          body="The first farms are being verified now. Check back in a few days."
        />
      )}
    </section>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-card border border-dashed border-soil-300 bg-white/50 p-14 text-center">
      <Sprout className="mx-auto size-9 text-soil-300" aria-hidden />
      <h3 className="mt-4 font-display text-lg font-bold text-soil-800">{title}</h3>
      <p className="mx-auto mt-1.5 max-w-sm text-sm text-soil-500">{body}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   How it works
   ═══════════════════════════════════════════════════════════════════════ */

const STEPS = [
  {
    number: '01',
    title: 'The farmer posts, or we do',
    body: "A farmer lists their own produce, or tells us what's ready and we list it for them. Either way, we verify the farm before anything goes live.",
  },
  {
    number: '02',
    title: 'You see where it came from',
    body: 'Village, district, farmer, growing practice — and often photos or video from the field itself. Then you add it to your basket.',
  },
  {
    number: '03',
    title: 'It gets picked for you',
    body: 'Your order reaches us the moment you place it. Most produce is harvested the same day it ships.',
  },
  {
    number: '04',
    title: 'You pay at the door',
    body: 'Cash or UPI, once the produce is in your hands and you have seen it. Not before.',
  },
];

function HowItWorks() {
  return (
    <section className="border-y border-soil-200 bg-soil-950 bg-grain py-24 text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-14 max-w-2xl">
          <h2 className="font-display text-display font-black">
            No mandi. No middleman.
            <br />
            <span className="text-harvest-400">No mystery.</span>
          </h2>
          <p className="mt-4 text-lg text-soil-400">
            Produce usually passes through four hands before it reaches you, and each one takes a
            cut and adds a day. We removed the hands.
          </p>
        </Reveal>

        <Stagger className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((step) => (
            <StaggerItem key={step.number}>
              <div className="relative">
                <span className="font-display text-5xl font-black text-farm-800">
                  {step.number}
                </span>
                <h3 className="mt-3 font-display text-lg font-bold text-white">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-soil-400">{step.body}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Farmer CTA
   ═══════════════════════════════════════════════════════════════════════ */

function FarmerCta() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-farm-600 via-farm-700 to-farm-800 p-10 sm:p-16">
          <div className="absolute inset-0 bg-grain opacity-40" aria-hidden />
          <div
            className="absolute -right-20 -top-20 size-72 rounded-full bg-harvest-400/20 blur-3xl"
            aria-hidden
          />

          <div className="relative max-w-2xl">
            <Badge variant="field" size="md">
              <Sprout aria-hidden /> For farmers
            </Badge>

            <h2 className="mt-5 font-display text-display font-black text-white">
              You grew it. You should price it.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-farm-100">
              List your produce, set your own price, and let us handle delivery and payment. Your
              name goes on every listing. Your contact details never do.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/auth/sign-up" search={{ role: 'farmer' }}>
                <Button size="lg" variant="harvest" className="group">
                  Start selling
                  <ArrowRight
                    className="transition-transform group-hover:translate-x-1"
                    aria-hidden
                  />
                </Button>
              </Link>
              <a href="mailto:sreerasaproducts@gmail.com">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 hover:text-white"
                >
                  Talk to us first
                </Button>
              </a>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
